import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import BookingService from "services/booking.service";
import RefundForm from "./RefundForm";
import { MODAL, modalActions } from "redux/slices/modal";
import SubscriptionService from "services/subscription.service";
import { change } from "redux-form";
import ToasterService from "utils/toaster.util";
import { digitFixer } from "utils/digitfixer.util";
import {
  getBookingPaymentReferencePrefix,
  isStripeBooking,
} from "utils/paymentGateway.util";

export default function Refund() {
  const form = "RefundForm";
  const dispatch = useAppDispatch();
  const booking = useAppSelector((state) => state.booking.booking);
  const bookingRefundOptions = useAppSelector(
    (state) => state.booking.bookingRefundOptions
  );
  const payment_method = useAppSelector(
    (state) => state.form?.[form]?.values?.payment_method
  );
  const payment_intent = useAppSelector(
    (state) => state.form?.[form]?.values?.payment_intent
  );
  const amountValue = useAppSelector(
    (state) => state.form?.[form]?.values?.amount
  );

  const isStripeOnlineRefund =
    isStripeBooking(booking) && payment_method === "Online System";
  const displayCurrency =
    booking?.price_details?.currency_short?.trim() || "SAR";

  useEffect(() => {
    if (payment_intent) {
      const priceInfo = bookingRefundOptions.find(
        (item) => item.value === payment_intent
      );
      const price = digitFixer(
        priceInfo?.data?.price - priceInfo?.data?.refundedPrice
      );
      dispatch(change(form, "amount", price || 0));
      dispatch(
        change(
          form,
          "payment_reference",
          `${getBookingPaymentReferencePrefix(booking)} ${priceInfo.value}`
        )
      );
    }
  }, [payment_method, payment_intent]);

  const refundAmount = useMemo(() => Number(amountValue) || 0, [amountValue]);

  const handleSubmit = async (values: any) => {
    const newValues = { ...values };
    newValues.amount = Number(newValues.amount);

    const data = { ...newValues };
    if (data.payment_intent) {
      const priceInfo = bookingRefundOptions.find(
        (item) => item.value === payment_intent
      );
      const price = digitFixer(
        priceInfo?.data?.price - priceInfo?.data?.refundedPrice
      );
      if (data.amount > price) {
        ToasterService.showError("Price can not be greater then paid price!");
        return;
      }
      data.refundedInfo = {
        isStripePayment: isStripeBooking(booking),
        trip: priceInfo.data.trip,
        price: priceInfo.data.price,
        payment_intent: priceInfo.value,
        merchant_ref:
          priceInfo.data.trip === "extension"
            ? priceInfo.data?.merchant_ref
            : null,
        extend_id:
          priceInfo.data.trip === "extension"
            ? priceInfo.data?.extend_id
            : null,
        paidBy:
          priceInfo.data.trip === "extension"
            ? priceInfo.data?.paidBy
            : null,
      };
      delete data.payment_intent;
    }

    const confirmMessage = isStripeOnlineRefund
      ? displayCurrency === "SAR"
        ? `Refund ${refundAmount.toFixed(2)} SAR via Stripe?`
        : `Refund ${refundAmount.toFixed(2)} ${displayCurrency} via Stripe (original charge currency)?`
      : "Do you really want to refund this amount?";

    dispatch(
      modalActions.openModal({
        width: "500px",
        type: MODAL.CONFIRMATION_FORM,
        data: {
          heading: "Refund Amount",
          message: confirmMessage,
        },
      })
    );

    SubscriptionService.subscribe(() => {
      BookingService.refundBooking(booking?._id, data);
    });
  };

  return (
    <div>
      <h3>Refund Booking Payment</h3>
      <RefundForm onSubmit={handleSubmit} />
    </div>
  );
}
